import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { useTranslation } from "react-i18next";
import "@splidejs/splide/css";
import {
  getGlobalSearchGroupURL,
  getSearchSuggestURL,
  getGlobalSearchQueryURL,
  productPrice,
  productPriceProdUrl,
  isPriceHiddenForLocale,
} from "../../constants";

const B2C_GROUPS_URL = getGlobalSearchGroupURL;
const B2C_SUGGEST_URL = getSearchSuggestURL;
const GLOBAL_QUERY_URL = getGlobalSearchQueryURL;
const ROWS = 10;
const QUERY_ROWS = 100;
const PRICE_API = productPrice;
const FEATURE_ROWS = 10;

const WD_BASE = "https://www.westerndigital.com";
const STUDIO_BASE = "https://studio.westerndigital.com";

const toAbs = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//.test(s)) return "https:" + s;
  if (/^[\w.-]+\//.test(s)) return "https://" + s;
  return WD_BASE + (s.startsWith("/") ? s : "/" + s);
};
const toDataSheetAbs = (p) => {
  if (!p) return "";
  const s = String(p).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return STUDIO_BASE + (s.startsWith("/") ? s : "/" + s);
};

const extractSkuFromUrlish = (u) => {
  if (!u) return "";
  try {
    const href = toAbs(u);
    const url = new URL(href);
    const sku =
      url.searchParams.get("sku") ||
      url.searchParams.get("code") ||
      url.searchParams.get("productCode") ||
      url.searchParams.get("id");
    return (sku || "").trim();
  } catch {
    const m = String(u).match(/[?&](sku|code|productCode|id)=([^&]+)/i);
    return m ? decodeURIComponent(m[2]) : "";
  }
};

const stripHtml = (raw) => {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (!s) return "";
  if (!/[<&]/.test(String(s))) return String(s);
  const div = document.createElement("div");
  div.innerHTML = String(s);
  return (div.textContent || div.innerText || "")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const useDebouncedValue = (val, delay = 300) => {
  const [v, setV] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setV(val), delay);
    return () => clearTimeout(t);
  }, [val, delay]);
  return v;
};

const buildUrlFreshSearch = (q) => `?q=${encodeURIComponent(q)}`;
const buildSupportSearchUrl = (loc, term) => {
  const l = (loc || "").toLowerCase();
  const prefix = l && l !== "en-us" ? `/${l}` : "";
  return `${prefix}/support/search-support?q=${encodeURIComponent(
    (term || "").trim()
  )}`;
};

const parseFiltersFromSearch = (search) => {
  const sp = new URLSearchParams(search);
  const raw = sp.get("fs");
  return raw
    ? raw
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
};
const buildUrlWithState = (q, p, filters) => {
  const sp = new URLSearchParams();
  sp.set("q", q);
  sp.set("p", String(p));
  if (Array.isArray(filters) && filters.length) sp.set("fs", filters.join("|"));
  return `?${sp.toString()}`;
};
const shallowEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler?.(e);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
};

// Price is shown only when (AEM header, #commerceLoad):
// data-purchasable-catalog="true" && data-purchasable-catalog-b2c="false"
const readPurchasableCatalog = () => {
  const el = document.getElementById("commerceLoad");
  const attr = (name) =>
    (el && el.getAttribute(name)) ??
    document.querySelector(`[${name}]`)?.getAttribute(name);
  return (
    attr("data-purchasable-catalog") === "true" &&
    attr("data-purchasable-catalog-b2c") === "false"
  );
};

// Header (with #commerceLoad) is injected later from AEM
const usePurchasableCatalog = () => {
  const [ok, setOk] = useState(readPurchasableCatalog);
  useEffect(() => {
    const update = () => setOk(readPurchasableCatalog());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-purchasable-catalog", "data-purchasable-catalog-b2c"],
    });
    return () => obs.disconnect();
  }, []);
  return ok;
};

const getLocaleFromPath = (pathname) => {
  const seg = (pathname.split("/").filter(Boolean)[0] || "").toLowerCase();
  return /^[a-z]{2}-[a-z]{2}$/.test(seg) ? seg : "en-us";
};

const bucketize = (payload) => {
  const out = { Products: [], Explore: [], Support: [], Categories: [] };
  const by = payload && typeof payload === "object" ? payload : {};

  const group = (name) =>
    by[name] ||
    by[name?.toLowerCase?.()] ||
    by[name?.toUpperCase?.()] ||
    by[name?.replace?.(/\b\w/g, (m) => m.toUpperCase())] ||
    [];

  const groupByRegex = (rx) =>
    Object.keys(by).reduce(
      (arr, k) => (rx.test(k) ? arr.concat(by[k] || []) : arr),
      []
    );

  const productsDocs = [...group("Products")];

  out.Products = productsDocs
    .filter((d) => d?.primaryImage || d?.title)
    .slice(0, 4);

  let newProducts = out?.Products;
  const catsRaw = newProducts.flatMap((d) => {
    const result = [];

    if (d?.productCategoryTitle_s && (d?.productCategoryPagePath_s || d?.url)) {
      result.push({
        title: d.productCategoryTitle_s,
        url: d.productCategoryPagePath_s || d.url,
        type: "category",
      });
    }

    if (
      d?.breadcrumbSubCategoryTitle_s &&
      (d?.breadcrumbSubCategoryPagePath_s || d?.url)
    ) {
      result.push({
        title: d.breadcrumbSubCategoryTitle_s,
        url: d.breadcrumbSubCategoryPagePath_s || d.url,
        type: "subcategory",
      });
    }
    return result;
  });

  const seenCat = new Set();
  out.Categories = catsRaw
    .filter((x) => {
      const k = String(x.title).toLowerCase();
      if (seenCat.has(k)) return false;
      seenCat.add(k);
      return true;
    })
    .slice(0, 5);

  const pr = group("Press Release").concat(group("Press Releases"));
  const web = group("Web");
  const webSolutions = web.filter((x) => {
    const st = Array.isArray(x?.filterSubDocumentType)
      ? x.filterSubDocumentType[0]
      : x?.filterSubDocumentType;
    const u = x?.url || x?.id || "";
    return (
      /solution/i.test(String(st || "")) || /\/solutions\//i.test(String(u))
    );
  });
  const documents = group("Documents").concat(
    groupByRegex(
      /white\s*paper|case\s*study|data\s*sheet|product\s*manual|solution\s*brief|technology\s*brief|document/i
    )
  );
  const blogs = group("Blogs").concat(groupByRegex(/blog/i));

  const extraWeb = web.filter((x) => !webSolutions.includes(x));
  const events = group("Events");
  const mediaKits = group("Media Kits");
  const knowledge = group("Knowledge Base")
    .concat(group("KnowledgeBase"))
    .concat(group("KB"));
  const community = group("Community");
  const support = group("Support")
    .concat(group("Support Products"))
    .concat(group("Software Downloads"));
  const others = Object.keys(by)
    .filter(
      (k) =>
        ![
          "Products",
          "Blogs",
          "Documents",
          "Web",
          "Press Release",
          "Press Releases",
          "Support",
          "Support Products",
          "Software Downloads",
          "Knowledge Base",
          "KnowledgeBase",
          "KB",
          "Community",
          "Events",
          "Media Kits",
          "Variant",
        ].includes(k)
    )
    .flatMap((k) => by[k] || []);

  const MAX = 5;
  const explore = [];
  const seenKey = new Set();
  const keyOf = (x) => x?.id || x?.url || JSON.stringify(x);

  // helper to add if not duplicate
  const addExplore = (item) => {
    if (!item) return;
    const k = keyOf(item);
    if (!k || seenKey.has(k)) return;
    seenKey.add(k);
    if (explore.length < MAX) explore.push(item);
  };

  // 1. Always include top Press Release (if present)
  if (pr.length > 0) {
    addExplore(pr[0]);
  }

  // 1. Always include top Press Release (if present)
  if (pr.length > 0) {
    addExplore(pr[0]);
  }
  const cleanTitle = (t) =>
    String(t || "").replace(/\s*\|\s*Western Digital$/, "");

  // 2. From Web: pick unique filterSubDocumentType
  const limit = pr.length > 0 ? 4 : 5;
  const webByType = {};
  for (const item of web) {
    const type = Array.isArray(item.filterSubDocumentType)
      ? item.filterSubDocumentType[0]
      : item.filterSubDocumentType || "Other";

    // sanitize title here
    item.title = cleanTitle(
      Array.isArray(item.title) ? item.title[0] : item.title
    );

    if (!webByType[type]) {
      webByType[type] = item; // keep the first (highest score is usually first)
    }
  }

  Object.values(webByType).slice(0, limit).forEach(addExplore);

  // 3. If still less than required, fill with top scored Web items (regardless of type)
  if (explore.length < MAX) {
    const remainingWeb = web.sort((a, b) => (b.score || 0) - (a.score || 0));
    for (const item of remainingWeb) {
      if (explore.length >= MAX) break;
      addExplore(item);
    }
  }

  // 4. If still less than 5, use fallback groups (like before)
  const add = (arr) => {
    for (let i = 0; i < arr.length && explore.length < MAX; i++) {
      addExplore(arr[i]);
    }
  };

  add(extraWeb);
  add(events);
  add(mediaKits);
  add(knowledge);
  add(community);
  add(support);
  add(others);

  out.Explore = explore;

  let supportObj = [
    (by["Support Products"] || [])[0],
    (by["Software Downloads"] || [])[0],
    (by["Knowledge Base"] || [])[0],
    (by["Documents"] || [])[0],
    (by["Community"] || [])[0],
  ].filter(Boolean);
  out.Support = supportObj.splice(0, 5);
  return out;
};

const parseFacetArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += 2)
    out.push({ name: arr[i], count: arr[i + 1] || 0 });
  return out;
};
const mergeFacetBuckets = (arr) => {
  const acc = new Map();
  (arr || []).forEach((f) => {
    const raw = (f?.name ?? "").trim();
    if (!raw) return;
    const key = raw.toLocaleLowerCase();
    const cnt = Number(f?.count || 0);
    const prev = acc.get(key);
    if (prev) prev.count += cnt;
    else acc.set(key, { name: raw, count: cnt });
  });
  return Array.from(acc.values())
    .filter((f) => f.name.toLocaleLowerCase() !== "variant")
    .sort((a, b) => b.count - a.count);
};
const normalizeQueryResponse = (json) => {
  const parseFacetField = (root, key) => {
    const raw = root && root[key];
    if (!raw) return [];
    if (Array.isArray(raw)) {
      if (raw.length && typeof raw[0] === "object") {
        return raw
          .map((x) => ({
            name: (x.docType || x.name || x.value || x.doc || "").trim(),
            count: Number(x.docCount || x.count || x.freq || 0),
          }))
          .filter((x) => x.name);
      }
      return parseFacetArray(raw);
    }
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([k, v]) => ({
        name: String(k),
        count: Number(v || 0),
      }));
    }
    return [];
  };

  const facetRoot =   (json && json.facet_counts && json.facet_counts.facet_fields) || json?.facet_fields ||  {};
  const facetDocType = parseFacetField(facetRoot, "documentType");
  const facetSubType = parseFacetField(facetRoot, "filterSubDocumentType");
  const flatFacets = mergeFacetBuckets([...facetDocType, ...facetSubType]);

  const totalFromSubType = (facetSubType || []).reduce(
    (sum, f) => sum + (Number(f.count) || 0),
    0
  );

  const variantCount = (facetSubType || [])
    .filter((f) => String(f.name || "").toLowerCase() === "variant")
    .reduce((sum, f) => sum + (Number(f.count) || 0), 0);

  const filterOutVariant = (d) => {
    const st = Array.isArray(d?.filterSubDocumentType)
      ? d.filterSubDocumentType[0]
      : d?.filterSubDocumentType;
    return String(st || "").toLowerCase() !== "variant";
  };

  if (Array.isArray(json?.docs)) {
    const rawList = json.docs || [];
    const list = rawList.filter(filterOutVariant);
    const totalRaw = totalFromSubType || Number(json?.total || json?.numFound || 0) || rawList.length;
    const total = Math.max(0, totalRaw - variantCount);
    return { list, flatFacets, total };
  }

  const toGroupMap = (raw) => {
    const map = {};
    if (!raw) return map;
    if (Array.isArray(raw)) {
      raw.forEach((g) => {
        const k = g?.groupValue || g?.grpDocumentType || g?.key || g?.name;
        const docs = g?.doclist?.docs || g?.docs || [];
        if (k) map[k] = (map[k] || []).concat(docs);
      });
      return map;
    }
    Object.entries(raw).forEach(([k, v]) => {
      if (Array.isArray(v)) map[k] = v;
      else if (v?.doclist?.docs) map[k] = v.doclist.docs;
      else if (Array.isArray(v?.groups)) {
        const docs = [];
        v.groups.forEach((g) => docs.push(...(g?.doclist?.docs || [])));
        map[k] = docs;
      } else if (v?.docs) map[k] = v.docs;
    });
    return map;
  };

  const groupedRaw =
    json?.grouped_docs ||
    json?.groupedResults ||
    json?.grouped ||
    json?.groups ||
    json?.data?.grouped ||
    json?.data?.groups ||
    {};
  const grouped = toGroupMap(groupedRaw);

  const rawList = Object.keys(grouped)
    .filter((k) => k !== "Products")
    .flatMap((k) => grouped[k] || []);
  const list = rawList.filter(filterOutVariant);

  const totalRaw = totalFromSubType || Number(json?.total || json?.numFound || 0) || rawList.length;
  const total = Math.max(0, totalRaw - variantCount);
  return { list, flatFacets, total };
};

const buildPageList = (page, total) => {
  if (total <= 8) return Array.from({ length: total }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, "dots", total];
  if (page >= total - 3)
    return [1, "dots", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "dots", page - 1, page, page + 1, "dots", total];
};

export default function SearchLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = useMemo(
    () => getLocaleFromPath(location.pathname),
    [location.pathname]
  );
  const lastSearchKeyRef = useRef("");
    // No price for es-es (PRICE_HIDDEN_LOCALES) or when purchasable-catalog condition fails
  const canShowPrice = usePurchasableCatalog();
  const hidePrice = isPriceHiddenForLocale(locale) || !canShowPrice;
  const nextRunOptionsRef = useRef({
    updateFacets: true,
    refreshProducts: true,
  });
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language?.toLowerCase() !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  //For filter section mobile view
  const [isOpenMobFiltr, setIsOpenMobFiltr] = useState(false);
  const filterItemWrapRef = useRef(null);
  const filterHeaderRef = useRef(null);
  const filterBtnRef = useRef(null);

  useEffect(() => {
    if (isOpenMobFiltr) {
      document.body.classList.add("overflow-hidden");
      // Calculate dynamic height
      if (
        filterItemWrapRef.current &&
        filterHeaderRef.current &&
        filterBtnRef.current
      ) {
        const winHeight = window.innerHeight;
        const headerHeight = Math.abs(
          filterHeaderRef.current.offsetHeight || 0
        );
        const btnHeight = Math.abs(filterBtnRef.current.offsetHeight || 0);
        filterItemWrapRef.current.style.height = `${
          winHeight - headerHeight - btnHeight
        }px`;
      }
    } else {
      document.body.classList.remove("overflow-hidden");
      if (filterItemWrapRef.current) {
        filterItemWrapRef.current.style.height = "";
      }
    }
  }, [isOpenMobFiltr]);

  // search box
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 300);

  // predictive overlay
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [buckets, setBuckets] = useState({
    Products: [],
    Explore: [],
    Support: [],
    Categories: [],
  });

  // results
  const [queryLoading, setQueryLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState({ list: [] });
  const [flatFacets, setFlatFacets] = useState([]);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeQuery, setActiveQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const productsTopRef = useRef(null);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));

  const hasQuery = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return !!(sp.get("q") || "").trim();
  }, [location.search]);
  const showResults = hasQuery || hasSearched;
  useEffect(() => {
    let aborted = false;
    const term = (dq || "").trim();
    if (term.length < 3) {
      setSuggestions([]);
      setBuckets({ Products: [], Explore: [], Support: [], Categories: [] });
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        // query for search
        const groupUrl = new URL(B2C_GROUPS_URL);
        groupUrl.searchParams.set("q", term);
        groupUrl.searchParams.set("locale", locale);
        groupUrl.searchParams.set(
          "fl",
          "id,url,productCategoryPagePath_s,productCategoryTitle_s,breadcrumbSubCategoryPagePath_s,breadcrumbSubCategoryTitle_s,title,primaryImage,baseProductId_s,filterSubDocumentType"
        );
        groupUrl.searchParams.set("groupBy", "grpDocumentType");
        groupUrl.searchParams.set("groupLimit", "50");
        //groupUrl.searchParams.set("documentType", "Products,Support Products,Software Downloads,Knowledge Base,Community,documents");
        groupUrl.search +=
          (groupUrl.search ? "&" : "?") +
          "documentType=" +
          [
            "Products",
            "Support Products",
            "Software Downloads",
            "Knowledge Base",
            "Community",
            "documents",
          ].join(",");
        groupUrl.searchParams.set("channel", "B2C");
        const suggUrl = new URL(B2C_SUGGEST_URL);
        suggUrl.searchParams.set("q", term);
        suggUrl.searchParams.set("locale", locale);
        suggUrl.searchParams.set("rows", "5");
        suggUrl.searchParams.set("documentType", "products");
        suggUrl.search += (suggUrl.search ? "&" : "?") + "fq=filterBy:products";

        const [gRes, sRes] = await Promise.all([
          fetch(groupUrl),
          fetch(suggUrl),
        ]);
        if (aborted) return;

        const gJson = await gRes.json();
        const sJson = await sRes.json();

        const groups =
          gJson?.groupedResults ||
          gJson?.grouped ||
          gJson?.groups ||
          gJson?.grouped_docs ||
          {};
        setBuckets(bucketize(groups));

        const sugs = sJson?.suggestions || sJson?.docs || sJson?.data || [];
        const normalized = sugs
          .map((x) => {
            const title = x?.title || x?.name || x?.term || x?.label || "";
            const url =
              x?.url ||
              x?.productCategoryPagePath_s ||
              x?.breadcrumbSubCategoryPagePath_s ||
              "#";
            return title ? { title, url } : null;
          })
          .filter(Boolean)
          .slice(0, 5);
        setSuggestions(normalized);
      } catch (err) {
        console.error("predictive search error", err);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [dq, locale]);
  const [priceMap, setPriceMap] = useState({});

  const priceFetchedRef = useRef(new Set());
  const priceInflightRef = useRef(new Map());
  const priceAbortRef = useRef(null);
  const firstFormattedInObject = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (
        v &&
        typeof v === "object" &&
        typeof v.formattedValue === "string" &&
        v.formattedValue
      ) {
        return v.formattedValue;
      }
    }
    return "";
  };

  const pricePair = (v, f, currencySymbol = "$") => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) return null;
    const text = (typeof f === "string" && f) ? f : `${currencySymbol}${n.toFixed(2)}`;
    return { val: n, text };
  };

  const collectPricePairs = (p = {}) => {
    const vals = [];

    const pushFV = (fv) => {
      if (typeof fv === "string" && fv.trim()) vals.push(fv.trim());
    };

    if (p?.discountPriceData) pushFV(p.discountPriceData.formattedValue);

    if (p?.superDiscountPriceData && typeof p.superDiscountPriceData === "object") {
      Object.values(p.superDiscountPriceData).forEach(v => pushFV(v?.formattedValue));
    }

    if (p?.lowestPriceData) pushFV(p.lowestPriceData.formattedValue);
    if (p?.priceData) pushFV(p.priceData.formattedValue);
    if (p?.priceSummary?.finalPrice) pushFV(p.priceSummary.finalPrice.formattedValue);
    if (p?.price) pushFV(p.price.formattedValue);
    if (p?.priceInfo) pushFV(p.priceInfo.formattedValue);

    return vals;
  };

  const pickLowestPricePair = (p = {}) => {
    const fvs = collectPricePairs(p);
    if (!fvs.length) return { val: Number.NaN, text: "" };

    let bestTxt = "";
    let bestNum = Number.POSITIVE_INFINITY;

    for (const raw of fvs) {
      const s = String(raw);
      const only = s.replace(/[^\d.,\-]/g, "");
      let num = Number.NaN;

      if (only.includes(",") && only.includes(".")) {
        const decIsComma = only.lastIndexOf(",") > only.lastIndexOf(".");
        if (decIsComma) {
          num = Number(only.replace(/\./g, "").replace(",", "."));
        } else {
          num = Number(only.replace(/,/g, ""));
        }
      } else if (only.includes(",")) {
        const after = only.length - only.lastIndexOf(",") - 1;
        if (after === 2) num = Number(only.replace(",", "."));
        else num = Number(only.replace(/,/g, ""));
      } else {
        num = Number(only);
      }

      if (Number.isFinite(num) && num < bestNum) {
        bestNum = num;
        bestTxt = s;
      }
    }

    if (!Number.isFinite(bestNum)) return { val: Number.NaN, text: "" };
    return { val: bestNum, text: bestTxt };
  };

  const pickFormattedPrice = (p = {}) => pickLowestPricePair(p).text;

  const parseAndLogPrice = (code, prodLike = {}) => {
    const priceText = pickFormattedPrice(prodLike);
    const clearance = !!(
      prodLike?.badges?.includes?.("CLEARANCE") ||
      prodLike?.promotionType === "CLEARANCE" ||
      prodLike?.clearance === true
    );

    const promoText =
      prodLike?.summaryMessage ||
      prodLike?.promoMessage ||
      prodLike?.message ||
      "";

    const detailsUrl = prodLike?.promotionUrl || prodLike?.detailsUrl || "";

    return { priceText, clearance, promoText, detailsUrl };
  };

  const fetchPriceBatch = async (codesInput) => {
    const uniq = Array.from(
      new Set((codesInput || []).map(String).filter(Boolean))
    );
    if (!uniq.length) return;
    const missing = uniq.filter(
      (c) => !priceFetchedRef.current.has(c) && !priceMap[c]
    );
    if (!missing.length) return;
    const key = missing.slice().sort().join("|");
    if (priceInflightRef.current.has(key)) {
      await priceInflightRef.current.get(key);
      return;
    }

    missing.forEach((c) => priceFetchedRef.current.add(c));

    const controller = new AbortController();
    priceAbortRef.current = controller;

    const run = (async () => {
      try {
        const regex = /^[a-z]{2}(?:-[a-z]{2})?$/i;
        const location = window.location.href.split("/")[3];
        const locale = regex.test(location) ? `${location}/` : "";
        const new_price_url =
          productPriceProdUrl + locale +"store/cart/guest/products/priceAndInventory";

        const norm = (s) =>
          (s || "")
            .toString()
            .toLowerCase()
            .replace(/[™®]/g, "")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();

        const baseTokens = (base) => {
          const stop = new Set([
            "wd",
            "western",
            "digital",
            "pc",
            "desktop",
            "drive",
            "drives",
            "hard",
            "hdd",
            "ssd",
            "sata",
            "nvme",
            "usb",
            "product",
            "products",
            "platform",
            "platforms",
            "data",
            "center",
            "series",
          ]);
          return base
            .toLowerCase()
            .split(/[^a-z0-9]+/g)
            .filter(Boolean)
            .filter((t) => (t.length >= 4 || /\d/.test(t)) && !stop.has(t));
        };

        // Step 1: Create an array of fetch promises
        const fetches = missing.map(async (c) => {
          const url = new URL(new_price_url);
          url.searchParams.set("fields", "FULL");
          url.searchParams.set("productsQuery", c);

          const res = await fetch(url.toString(), {
            signal: controller.signal,
            headers: { Accept: "application/json" },
          });

          const json = await res.json();
          const arr =
            (Array.isArray(json?.products) && json.products) ||
            (Array.isArray(json) && json) ||
            (json?.product ? [json.product] : []);
          let bestVal = Number.POSITIVE_INFINITY;
          let bestText = "";
          for (const p of (arr || [])) {
            const pair = pickLowestPricePair(p);
            if (Number.isFinite(pair.val) && pair.val < bestVal) {
              bestVal = pair.val;
              bestText = pair.text || "";
            }
          }
          if (!Number.isFinite(bestVal)) {
            for (const p of (arr || [])) {
              const txt = pickFormattedPrice(p);
              if (txt) { bestText = txt; break; }
            }
          }


          return [
            c,
            parseAndLogPrice(c, {
              priceData: { value: bestVal, formattedValue: bestText },
            }),
          ];

        });

        // Wait for fetching the price for the products
        const results = await Promise.all(fetches);

        // Combine into a single result
        const updates = Object.fromEntries(results);

        // Update your state
        setPriceMap((prev) => ({ ...prev, ...updates }));
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("[PRICE] batch failed", e);
          const fallback = {};
          missing.forEach((c) => (fallback[c] = { priceText: "—" }));
          setPriceMap((prev) => ({ ...prev, ...fallback }));
        }
      } finally {
        priceInflightRef.current.delete(key);
      }
    })();

    // priceInflightRef.current.set(key, run);
    await run;
  };
  const fetchPricesForDocs = async (docs) => {
    if (isPriceHiddenForLocale(locale)) return;
    const codes = new Set();
    (docs || []).forEach((d) => {
      const base =
        (Array.isArray(d?.baseProductId_s)
          ? d.baseProductId_s[0]
          : d?.baseProductId_s) || "";
      const sku = extractSkuFromUrlish(d?.url || d?.id);
      if (base) codes.add(base);
      if (sku && sku !== base) codes.add(sku);
    });
    await fetchPriceBatch([...codes]);
  };
  useEffect(() => {
    if (Array.isArray(buckets?.Products) && buckets.Products.length) {
      fetchPricesForDocs(buckets.Products);
    }
  }, [buckets?.Products]);
  useEffect(() => {
    return () => {
      try {
        priceAbortRef.current?.abort?.();
      } catch {}
    };
  }, []);

  const pickBase = (d) =>
    (Array.isArray(d?.baseProductId_s)
      ? d.baseProductId_s[0]
      : d?.baseProductId_s) || "";
  const typeOf = (d) =>
    (Array.isArray(d?.filterSubDocumentType)
      ? d.filterSubDocumentType[0]
      : d?.filterSubDocumentType) || "";

  const isProductish = (d) => {
    const t = typeOf(d);
    const title = Array.isArray(d?.title) ? d.title[0] : d?.title || "";
    return (
      (t === "Products" || t === "Variant") &&
      !!pickBase(d) &&
      !/^data\s*sheet/i.test(title)
    );
  };

  const buildProductsFromDocs = (
    docs,
    limit = FEATURE_ROWS,
    deDupeByBase = true
  ) => {
    if (!Array.isArray(docs)) return [];
    if (!deDupeByBase) return docs.filter(isProductish).slice(0, limit);
    const seen = new Set();
    const out = [];
    for (const d of docs) {
      if (!isProductish(d)) continue;
      const base = pickBase(d);
      if (seen.has(base)) continue;
      seen.add(base);
      out.push(d);
      if (out.length >= limit) break;
    }
    return out;
  };

  const fetchProductsForQuery = async (term) => {
    // query for products
    const url = new URL(GLOBAL_QUERY_URL);
    url.searchParams.set("q", term);
    url.searchParams.set("locale", locale);
    url.searchParams.set(
      "fl",
      "baseProductId_s,supportPageUrl_s,dataSheet_s,id,title,description,primaryImage,filterSubDocumentType,url"
    );
    url.searchParams.set("rows", "100");
    url.searchParams.set("from", "0");
    url.searchParams.set("sort", "documentTypeDate");
    url.searchParams.set(
      "documentType",
      "Products,blogs,Promo,Events,Solutions,Business,Legal,Topics,Company"
    );
    url.search += (url.search ? "&" : "?") + "fq=documentType:Products";

    const res = await fetch(url.toString());
    const json = await res.json();
    return Array.isArray(json?.docs) ? json.docs : [];
  };
  const [resultProducts, setResultProducts] = useState([]);

  const runQuery = async (
    term,
    p = 1,
    filters = [],
    updateFacets = true,
    refreshProducts = true
  ) => {
    setQueryLoading(true);
    try {
      const buildSearchUrl = (page, filtersInput) => {
        const url = new URL(GLOBAL_QUERY_URL);
        url.searchParams.set("q", term);
        url.searchParams.set("locale", locale);
        url.searchParams.set(
          "fl",
          "baseProductId_s,supportPageUrl_s,dataSheet_s,id,title,description,primaryImage,filterSubDocumentType,url"
        );
        url.searchParams.set("groupBy", "filterSubDocumentType");
        url.searchParams.set("groupLimit", "20");
        url.searchParams.set("rows", String(ROWS));
        url.searchParams.set("from", String((page - 1) * ROWS));
        url.searchParams.set("sort", "documentTypeDate");
        url.searchParams.set(
          "documentType",
          "Products,solutions,blogs,Promo,Events,Solutions,Business,Legal,Topics,Company"
        );
        url.searchParams.set("facet", "on");
        url.searchParams.append("facet.field", "filterSubDocumentType");
        url.searchParams.append("facet.field", "documentType");
        url.searchParams.set("facet.mincount", "1");
        url.searchParams.set("facet.limit", "-1");
        url.searchParams.set("facet.sort", "count");

        if (Array.isArray(filtersInput) && filtersInput.length) {
          url.searchParams.append(
            "fq",
            `filterSubDocumentType:${filtersInput.join(",")}`
          );
        }

        return url;
      };

      const urlMain = buildSearchUrl(p, filters);
      const promises = [fetch(urlMain)];
      if (refreshProducts) promises.push(fetchProductsForQuery(term));
      const [resMain, prodsOnly] = await Promise.all(promises);

      const jsonMain = await resMain.json();
      let { list, flatFacets: ff, total } = normalizeQueryResponse(jsonMain);

      let implicitFilters = [];
      if ((!filters || !filters.length) && Array.isArray(ff) && ff.length) {
        implicitFilters = ff
          .map((f) => f.name)
          .filter(
            (name) =>
              name &&
              String(name).toLowerCase() !== "variant"
          );
      }

      if ((!filters || !filters.length) && implicitFilters.length) {
        const urlFiltered = buildSearchUrl(p, implicitFilters);
        const resFiltered = await fetch(urlFiltered);
        const jsonFiltered = await resFiltered.json();
        const normalizedFiltered = normalizeQueryResponse(jsonFiltered);
        list = normalizedFiltered.list;
        ff = normalizedFiltered.flatFacets;
        total = normalizedFiltered.total;
      }

      setResults({ list });
      setResultsTotal(total);
      setPage(p);
      setActiveQuery(term);
      if (updateFacets) setFlatFacets(ff);
      setHasSearched(true);

      if (refreshProducts) {
        let productDocs =
          prodsOnly && prodsOnly.length
            ? prodsOnly
            : buildProductsFromDocs(list, FEATURE_ROWS, true);

        setResultProducts(productDocs);
        fetchPricesForDocs(productDocs);
      }
    } catch (e) {
      console.error("globalsearchquery error", e);
      if (refreshProducts) setResultProducts([]);
    } finally {
      setQueryLoading(false);
    }
  };

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const urlQ = (sp.get("q") || "").trim();
    const urlP = Math.max(1, parseInt(sp.get("p") || "1", 10) || 1);
    const urlFs = parseFiltersFromSearch(location.search);
    if (!urlQ) return;

    const key = `${urlQ}|${urlP}|${locale}|${urlFs.join(",")}`;
    if (lastSearchKeyRef.current === key) return;
    lastSearchKeyRef.current = key;

    if (q !== urlQ) setQ(urlQ);
    setOpen(false);
    if (!shallowEqual(urlFs, selectedFilters)) {
      setSelectedFilters(urlFs);
    }

    const { updateFacets, refreshProducts } = nextRunOptionsRef.current || {};
    runQuery(
      urlQ,
      urlP,
      urlFs,
      updateFacets !== false,
      refreshProducts !== false
    );

    nextRunOptionsRef.current = { updateFacets: true, refreshProducts: true };
  }, [location.search, locale]);
  const submitSearch = (term) => {
    const t = (term || "").trim();
    if (!t) return;
    setOpen(false);
    setSelectedFilters([]);
    nextRunOptionsRef.current = { updateFacets: true, refreshProducts: true };
    navigate(buildUrlFreshSearch(t));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await submitSearch(q);
  };
  const gotoPage = async (p) => {
    if (!activeQuery) return;
    if (p < 1) p = 1;
    const totalPages = Math.max(1, Math.ceil(resultsTotal / ROWS));
    if (p > totalPages) p = totalPages;

    nextRunOptionsRef.current = { updateFacets: false, refreshProducts: false };
    navigate(buildUrlWithState(activeQuery, p, selectedFilters));
    setTimeout(() => {
      const el = productsTopRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 0);
  };

  const totalPages = Math.max(1, Math.ceil(resultsTotal / ROWS));
  const showOverlay =
    !!open &&
    (loading ||
      !!suggestions.length ||
      Object.values(buckets).some((b) => Array.isArray(b) && b.length > 0));
  const toggleFacet = async (name) => {
    setTimeout(() => {
      const el = productsTopRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 0);
    const value = String(name || "").trim();
    const next = selectedFilters.includes(value)
      ? selectedFilters.filter((v) => v !== value)
      : [...selectedFilters, value];
    setSelectedFilters(next);
    if (activeQuery) {
      nextRunOptionsRef.current = {
        updateFacets: false,
        refreshProducts: false,
      };
      navigate(buildUrlWithState(activeQuery, 1, next));
    }
  };

  const clearAllFilters = async () => {
    setSelectedFilters([]);
    if (activeQuery) {
      nextRunOptionsRef.current = {
        updateFacets: false,
        refreshProducts: false,
      };
      navigate(buildUrlWithState(activeQuery, 1, []));
    }
  };
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featured, setFeatured] = useState([]);
  const productsStripRef = useRef(null);
  const resultsStripRef = useRef(null);

  const fetchFeatured = async () => {
    setFeaturedLoading(true);
    try {
      const docs = await fetchProductsForQuery("*");

      let items = buildProductsFromDocs(docs, FEATURE_ROWS, false);
      if (!items.length) items = (docs || []).slice(0, FEATURE_ROWS);

      setFeatured(items);
      fetchPricesForDocs(items);
    } catch (e) {
      console.error("featured fetch error", e);
      setFeatured([]);
    } finally {
      setFeaturedLoading(false);
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 992px)");

    if (!mediaQuery.matches) return;

    function setHeights(currentSlide) {
      const storeResult = document.querySelector(".storeSearchResult");

      if (!storeResult) return;

      const splidWrapHeight = storeResult.offsetHeight;
      storeResult.style.height = `${splidWrapHeight}px`;
      const wrapper = currentSlide?.closest(".splide__list");

      if (wrapper) {
        const allSlides = wrapper.querySelectorAll(".splide__slide");
        allSlides.forEach((el) => {
          const wrapperEl = el.querySelector(".storeSearchWrapper");
          if (wrapperEl) {
            if (el === currentSlide) {
              wrapperEl.style.height = "";
            } else {
              wrapperEl.style.height = `${splidWrapHeight}px`;
            }
          }
        });
      }
    }

    function clearHeights() {
      const storeResult = document.querySelector(".storeSearchResult");
      if (storeResult) storeResult.style.height = "";

      document.querySelectorAll(".storeSearchWrapper").forEach((w) => {
        w.style.height = "";
      });
    }

    function handleFocusIn(e) {
      const active = e.target;

      if (active.classList.contains("prdct-title")) {
        const slide = active.closest(".splide__slide");
        if (!slide) return;

        setHeights(slide);

        const productCTA = slide.querySelector(".productCTAwrap");
        productCTA?.classList.add("productCTAwrapShow");
      }

      if (active.closest(".productCTAwrap")) {
        active.closest(".productCTAwrap")?.classList.add("productCTAwrapShow");
      }
    }

    function handleFocusOut() {
      const openCTA = document.querySelector(".productCTAwrapShow");
      if (!openCTA) return;

      requestAnimationFrame(() => {
        const newlyFocused = document.activeElement;

        if (!openCTA.contains(newlyFocused)) {
          openCTA.classList.remove("productCTAwrapShow");

          if (!newlyFocused.closest(".splide__list")) {
            clearHeights();
          }
        }
      });
    }

    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);

    return () => {
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  useEffect(() => {
    const focusOutFromSearchSuggestion = (e) => {
      const isTabOrShift = e.key === "Tab" || e.key === "Shift";
      const insideForm = e.target.closest(".predictive-search-form");
      if (isTabOrShift && !insideForm) {        
        setOpen(false);       
      }
    };
    document.addEventListener("keyup", focusOutFromSearchSuggestion);
    return () => {
      document.removeEventListener("keyup", focusOutFromSearchSuggestion);
    };
  }, []);

  useEffect(() => {
    if (hasQuery) return;
    fetchFeatured();
  }, [hasQuery, locale]);
  const scrollStrip = (ref, dir = 1) => {
    const node = ref.current;
    if (!node) return;
    node.scrollBy({ left: dir * 520, behavior: "smooth" });
  };
  const ProductCard = React.memo(({ doc, hovered }) => {
    const base = pickBase(doc);
    const img = toAbs(doc?.primaryImage);
    const title = Array.isArray(doc?.title)
      ? doc.title[0]
      : doc?.title || "Product";
    const hrefTitle = toAbs(doc?.url || doc?.id);
    const hrefLearnMore = toAbs(doc?.id || doc?.url);
    const dataSheet = doc?.dataSheet_s ? toDataSheetAbs(doc.dataSheet_s) : "";
    const supportUrl = doc?.supportPageUrl_s ? toAbs(doc.supportPageUrl_s) : "";

    const sku = extractSkuFromUrlish(doc?.url || doc?.id);
    const pinfo = priceMap[base] || priceMap[sku] || {};
    const priceText = pinfo.priceText ?? "";
    const isClearance = !!pinfo.clearance;
    const promoText = pinfo.promoText || "";
    const detailsUrl = pinfo.detailsUrl || "";

    const supportedLocales = [
      "cs-cz",
      "de-at",
      "de-de",
      "el-gr",
      "en-at",
      "en-ca",
      "en-gb",
      "en-ie",
      "en-se",
      "en-us",
      "es-es",
      "fr-ca",
      "fr-fr",
      "it-it",
      "ja-jp",
      "nl-nl",
      "pl-pl",
      "support",
      "search",
      "company",
    ];
    let isCommerce =   supportedLocales.includes((location.pathname.split("/")[1] || "").toLowerCase()) || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) || /^[a-z]{2}-[a-z]{2}$/.test((location.pathname.split("/")[1] || "").toLowerCase());
    return (
      <div className="flex flex-col wd-border wd-border-[#7f7f7f] bg-white h-full">
        <div className="md:h-full">
          <div
            className="wd-bg-[#f2f3f3] flex items-center justify-center mb-4"
            style={{ height: 140 }}
          >
            {img ? (
              <img
                src={img}
                alt={title}
                className="object-contain"
                style={{ maxHeight: 140, maxWidth: 140 }}
                loading="lazy"
              />
            ) : (
              <div style={{ height: 100 }} />
            )}
          </div>
          <a
            href={hrefTitle}
            className="text-blue wd-text-[12px] block px-2 mb-2 prdct-title"
            title={title}
          >
            {title}
          </a>
        </div>
        <div className="wd-text-[12px]">
          <div className="mt-auto px-2">
            {!hidePrice && (priceText && isCommerce ? (
              <>
                <div className="text-gray-500">{t("support.starting_at")}</div>
                <div className="font-bold mb-2">{priceText}</div>
              </>
            ) : (
              <span className="inline-block w-16 h-3 bg-gray-200 animate-pulse rounded" />
            ))}

            {promoText && (
              <div className="mt-1 wd-text-green-700 mb-2">
                {promoText}{" "}
                {detailsUrl && (
                  <a href={detailsUrl} className="underline">
                    {t("support.see_details")}
                  </a>
                )}
              </div>
            )}

            {isClearance && (
              <div className="wd-mt-1 wd-inline-block wd-bg-red-600 wd-text-white wd-text-[10px] wd-px-2 wd-py-0.5">
                {t("support.clearance")}
              </div>
            )}
          </div>
          <div
            className={`px-1 productCTAwrap ${
              hovered ? "productCTAwrapShow" : ""
            }`}
          >
            {dataSheet && (
              <a
                href={dataSheet}
                className="wd-py-2 wd-px-1 wd-whitespace-normal wd-cursor-pointer hover:wd-bg-[#f2f3f3] wd-block wd-text-black md:wd-text-[#465052] md:hover:wd-text-black"
              >
                {t("support.data_sheet")}
              </a>
            )}
            {supportUrl && (
              <a
                href={supportUrl}
                className="wd-py-2 wd-px-1 wd-whitespace-normal wd-cursor-pointer hover:wd-bg-[#f2f3f3] wd-block wd-text-black md:wd-text-[#465052] md:hover:wd-text-black"
              >
                {t("support.support")}
              </a>
            )}
            <a
              href={hrefLearnMore}
              className="wd-py-2 wd-px-1 wd-whitespace-normal wd-cursor-pointer hover:wd-bg-[#f2f3f3] wd-block wd-text-black md:wd-text-[#465052] md:hover:wd-text-black"
            >
              {t("support.learn_more")}
            </a>
          </div>
        </div>
      </div>
    );
  });

  const ProductsStrip = ({ docs }) => {
    if (!docs?.length) return null;

    const showArrows = docs.length > 6;
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [baseWrapHeight, setBaseWrapHeight] = useState(null);
    const [baseLiHeight, setBaseLiHeight] = useState(null);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    useEffect(() => {
      itemRefs.current = itemRefs.current.slice(0, docs.length);
    }, [docs]);

    const measureHeights = () => {
      const firstWrap = itemRefs.current[0];
      const wrapTarget = firstWrap
        ? firstWrap.firstElementChild || firstWrap
        : null;
      const wrapRect = wrapTarget ? wrapTarget.getBoundingClientRect() : null;
      setBaseWrapHeight(wrapRect ? Math.ceil(wrapRect.height) : null);

      const slides = containerRef.current
        ? containerRef.current.querySelectorAll(".splide__slide")
        : null;
      let maxLi = 0;
      if (slides && slides.length) {
        slides.forEach((sl) => {
          const r = sl.getBoundingClientRect();
          if (r.height > maxLi) maxLi = r.height;
        });
      }
      setBaseLiHeight(maxLi || null);
    };

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(measureHeights);
      });
      return () => cancelAnimationFrame(id);
    }, [docs]);

    useEffect(() => {
      const onResize = () => {
        measureHeights();
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);

    const options = {
      type: "slide",
      rewind: false,
      perPage: 5,
      perMove: 5,
      gap: "16px",
      arrows: showArrows,
      pagination: false,
      drag: true,
      speed: 500,
      breakpoints: {
        1280: { perPage: 5, perMove: 5, gap: "16px" },
        1024: { perPage: 4, perMove: 4, gap: "16px" },
        768: { perPage: 3, perMove: 3, gap: "12px" },
        640: { perPage: 2, perMove: 2, gap: "12px" },
        420: { perPage: 1, perMove: 1, gap: "8px" },
      },
    };

    return (
      <div className="mb-6">
        <div
          className="storeSearchResult"
          ref={containerRef}
          onMouseLeave={() => {
            setHoveredIndex(null);
          }}
          style={{
            height:
              hoveredIndex != null && baseLiHeight
                ? `${baseLiHeight}px`
                : undefined,
          }}
        >
          <Splide
            key={`splide-${docs.length}-${
              docs[0]?.id || docs[0]?.baseProductId_s || ""
            }`}
            aria-label="Products"
            options={options}
          >
            {docs.map((d, i) => {
              const isHovered = hoveredIndex === i;
              return (
                <SplideSlide
                  key={`slide-${i}-${pickBase(d) || d.id || d.url || i}`}
                  onMouseEnter={() => setHoveredIndex(i)}
                >
                  <div
                    className="storeSearchWrapper h-full"
                    ref={(el) => (itemRefs.current[i] = el)}
                    style={
                      hoveredIndex != null &&
                      i !== hoveredIndex &&
                      baseWrapHeight
                        ? { height: `${baseWrapHeight}px` }
                        : undefined
                    }
                  >
                    <ProductCard doc={d} hovered={isHovered} />
                  </div>
                </SplideSlide>
              );
            })}
          </Splide>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 max-w-[1200px] contain">
      <section>
        <div className="py-12 sm:py-20">
          <div className="wd-grid wd-grid-cols-12">
            <div className="wd-col-span-12 md:wd-col-span-10 md:wd-col-start-2">
              <h1 className="text-3xl md:text-5-6xl text-center font-medium">
                {t("support.search_title")}
              </h1>
              <div
                aria-controls="searchResultInnerWarp"
                className="hero-input mt-8 predictiveResultViewForm mx-auto r-wd-searchLanding"
                ref={wrapRef}
              >
                <form
                  name="searchForm"
                  className={`predictive-search-result-form ui-front input-group relative predictive-search-form ${
                    q ? "filled" : ""
                  }`}
                  onSubmit={onSubmit}
                >
                  <input
                    type="text"
                    id="hero-input-field"
                    name="q"
                    autoComplete="off"
                    placeholder={t("support.search_placeholder")}
                    aria-labelledby="searchBTN"
                    className="p-4 pr-24 pl-12 sm:pl-16 w-full rounded-none leading-medium"
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      if (!open) setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                  />

                  {showOverlay && (
                    <div className="predictiveResultView  searchLanding_r absolute bg-white text-black w-full z-20">
                      <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex md:items-start md:py-12 md:px-8 p-4">
                        {(suggestions.length > 0 ||
                          buckets.Categories.length > 0) && (
                          <div className="md:cols-3 md:px-4 row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex second left-slide">
                            {/* {suggestions.length > 0 && (
                              <>
                                <span className="cols-12 mb-6 text-base font-bold leading-heading inline-block">{t("support.suggestions")}</span>
                                <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex w-full">
                                  {suggestions.map((s, i) => (
                                    <ul key={`sug-${i}`} className="md:cols-12 list-reset lg:pr-2 xl:pr-7 text-xs md:text-sm">
                                      <li>
                                        <a
                                          href={buildUrlFreshSearch(s.title)}
                                          className="btn mb-6 inline-block leading-medium"
                                          onMouseDown={(e) => { e.preventDefault(); submitSearch(s.title, 1); }}
                                          onClick={(e) => { e.preventDefault(); submitSearch(s.title, 1); }}
                                        >
                                          {s.title}
                                        </a>
                                      </li>
                                    </ul>
                                  ))}
                                </div>
                              </>
                            )} */}

                            {buckets.Categories.length > 0 && (
                              <>
                                <span className="cols-12 mb-6 text-base font-bold leading-heading inline-block mt-2">
                                  {t("support.categories")}
                                </span>
                                <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex w-full">
                                  {buckets.Categories.map((c, i) => (
                                    <ul
                                      key={`cat-${i}`}
                                      className="md:cols-12 list-reset lg:pr-2 xl:pr-7 text-xs md:text-sm"
                                    >
                                      <li>
                                        <a
                                          href={toAbs(c.url)}
                                          className="btn mb-6 inline-block leading-medium predictive-search-class1"
                                        >
                                          {c.title}
                                        </a>
                                      </li>
                                    </ul>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {buckets.Products.length > 0 && (
                          <div className="md:cols-5 lg:cols-4 md:px-4 row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex second left-slide">
                            <span className="cols-12 mb-6 text-base leading-heading inline-block mt-2 md:mt-0">
                              <span className="font-bold">
                                {t("support.products")}
                              </span>{" "}
                              <a
                                href={buildUrlFreshSearch(q)}
                                className="underline text-sm text-grey-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  submitSearch(q, 1);
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  submitSearch(q, 1);
                                }}
                                aria-label={t("support.view_all_for", {
                                  query: q,
                                })}
                              >
                                {t("support.view_all")}
                              </a>
                            </span>
                            <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap flex w-full">
                              {buckets.Products.map((p, i) => {
                                const img = p?.primaryImage;
                                const title = Array.isArray(p?.title)
                                  ? p.title[0]
                                  : p?.title || "Product";
                                const url = p?.url || "#";
                                const base = pickBase(p);
                                const sku = extractSkuFromUrlish(
                                  p?.url || p?.id
                                );
                                const pinfo =
                                  priceMap[base] || priceMap[sku] || {};
                                const priceText = pinfo.priceText ?? "";
                                return (
                                  <ul
                                    key={`prod-${i}`}
                                    className="cols-6 list-reset lg:pr-2 xl:pr-7 text-xs"
                                  >
                                    <li>
                                      <a
                                        href={toAbs(url)}
                                        className="btn mb-8 md:mb-12 inline-block text-center leading-medium"
                                      >
                                        {img && (
                                          <img
                                            alt={title}
                                            src={toAbs(img)}
                                            width="117"
                                            height="117"
                                            className={`mb-1 search-result-product${i}`}
                                          />
                                        )}
                                        <div className="mb-1 text-xs leading-tight">
                                          {title}
                                        </div>
                                          {!hidePrice && (priceText ? (
                                            <>
                                              <div className="text-gray-500">{t("support.starting_at")}</div>
                                              <div className="font-bold">{priceText}</div>
                                            </>
                                          ) : (
                                            <span className="inline-block w-16 h-3 bg-gray-200 animate-pulse rounded" />
                                          ))}
                                      </a>
                                    </li>
                                  </ul>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {(buckets.Explore.length > 0 ||
                          buckets.Support.length > 0) && (
                          <div className="md:cols-5 md:px-4 row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex second left-slide">
                            {buckets.Explore.length > 0 && (
                              <>
                                <span className="cols-12 mb-6 text-base leading-heading inline-block">
                                  <span className="font-bold">
                                    {t("support.explore")}
                                  </span>{" "}
                                  <a
                                    href={buildUrlFreshSearch(q)}
                                    className="underline text-sm text-grey-3"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      submitSearch(q, 1);
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      submitSearch(q, 1);
                                    }}
                                    aria-label={t("support.view_all")}
                                  >
                                    {t("support.view_all")}
                                  </a>
                                </span>
                                <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex w-full">
                                  {buckets.Explore.map((d, i) => (
                                    <ul
                                      key={`exp-${i}`}
                                      className="md:cols-12 list-reset lg:pr-2 xl:pr-7 text-xs md:text-sm"
                                    >
                                      <li>
                                        <a
                                          href={toAbs(d?.url) || "#"}
                                          className="btn mb-6 inline-block leading-medium predictive-search-class1"
                                        >
                                          {(Array.isArray(d?.title)
                                            ? d.title[0]
                                            : d?.title) || "Explore"}
                                        </a>
                                      </li>
                                    </ul>
                                  ))}
                                </div>
                              </>
                            )}

                            {buckets.Support.length > 0 && (
                              <>
                                <span className="cols-12 mb-6 text-base leading-heading inline-block mt-2">
                                  <span className="font-bold">
                                    {t("support.support")}
                                  </span>{" "}
                                  <a
                                    href={buildSupportSearchUrl(locale, q)}
                                    className="underline text-sm text-grey-3"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setOpen(false);
                                      navigate(
                                        buildSupportSearchUrl(locale, q)
                                      );
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setOpen(false);
                                      navigate(
                                        buildSupportSearchUrl(locale, q)
                                      );
                                    }}
                                    aria-label={t("support.view_all")}
                                  >
                                    {t("support.view_all")}
                                  </a>
                                </span>
                                <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap md:flex w-full">
                                  {buckets.Support.map((d, i) => (
                                    <ul
                                      key={`sup-${i}`}
                                      className="md:cols-12 list-reset lg:pr-2 xl:pr-7 text-xs md:text-sm"
                                    >
                                      <li>
                                        <a
                                          href={toAbs(d?.url) || "#"}
                                          className="btn mb-6 inline-block leading-medium predictive-search-class1"
                                        >
                                          {(Array.isArray(d?.title)
                                            ? d.title[0]
                                            : d?.title) || "Support"}
                                        </a>
                                      </li>
                                    </ul>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <span className="search-action input-group-btn absolute pin-l pin-t text-base">
                    <button
                      type="submit"
                      id="searchBTN"
                      aria-label={t("common.search")}
                      tabIndex={-1}
                      className="btn-search btn pt-4 pb-3 px-4 sm:px-6"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        className="searchmag"
                      >
                        <g transform="translate(0.063 -0.127)">
                          <path d="M0,0H24V24H0Z" fill="none"></path>
                          <g transform="translate(1.921 2.11)">
                            <g fill="#fff" stroke="#000" strokeWidth="2">
                              <ellipse
                                cx="9.503"
                                cy="9.503"
                                rx="9.503"
                                ry="9.503"
                              ></ellipse>
                              <ellipse
                                cx="9.503"
                                cy="9.503"
                                rx="9.003"
                                ry="9.003"
                                fill="none"
                              ></ellipse>
                            </g>
                            <line
                              x1="3.77"
                              y1="3.77"
                              transform="translate(16.246 16.247)"
                              stroke="#000"
                              strokeLinecap="round"
                              strokeWidth="2"
                            ></line>
                          </g>
                        </g>
                      </svg>
                    </button>
                  </span>
                  <button
                    type="button"
                    className="underline text-sm absolute pin-r-12 svClearInput pin-t-15"
                    onClick={() => {
                      setQ("");
                      setSuggestions([]);
                      setBuckets({
                        Products: [],
                        Explore: [],
                        Support: [],
                        Categories: [],
                      });
                      setOpen(false);
                    }}
                  >
                    {t("common.clear")}
                  </button>
                  <button
                    type="button"
                    aria-label="Search Close"
                    className="search-collapse btn flex items-center"
                    onClick={() => setOpen(false)}
                  >
                    ✕
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      {!showResults && (
        <section className="section">
          <div className="mx-auto max-w-[1200px] pb-20">
            <div className="w-full">
              <h2 className="related-products-title uppercase text-xs bg-g-blue-1 px-5 py-1 mb-6 text-white inline-block font-normal mt-10 leading-normal heading6">
                {t("support.products_label")}
              </h2>
              {featuredLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 mb-8">
                  {Array.from({ length: FEATURE_ROWS }).map((_, i) => (
                    <div
                      key={`sk-${i}`}
                      className="h-64 border rounded bg-gray-100 animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <ProductsStrip docs={featured} refStrip={productsStripRef} />
              )}
            </div>
          </div>
        </section>
      )}
      {showResults && (
        <section className="section">
          <div className="mx-auto max-w-[1200px] py-16">
            <div className="flex flex-wrap grid-14 sm:grid-14 md:grid-14 lg:grid-14">
              <div
                onClick={() => setIsOpenMobFiltr(true)}
                className="sm:hidden flex-1 uppercase items-center mob-filter-button cursor-pointer w-full text-xxs py-2 border-2 border-black text-center text-white bg-black  mr-2"
              >
                Filter
              </div>
              {/* LEFT: filters */}
              <div
                className={`cols-13 sm:cols-3 left-filter fixed sm:relative pin-t pin-l z-10 sm:z-1 bg-white sm:block h-full ${
                  isOpenMobFiltr ? "" : "hidden"
                }`}
              >
                <div
                  className={`filter-overlay z-2 w-full h-full ${
                    isOpenMobFiltr ? "block" : ""
                  }`}
                />
                <div className="relative sm:z-1 z-10 bg-white h-full">
                  <div className="filterItemWrap relative overflow-auto sm:overflow-visible">
                    <div className="ui-front input-group">
                      <h2 className="border-b border-grey-2 mb-7 pb-4 hidden sm:block heading3">
                        {t("support.filtered_by")}
                      </h2>
                      <div className="pl-4 sm:pl-0 mt-16 sm:mt-0">
                        <div className="list-group kill-margin-bottom ">
                          <div className="relative mb-2 md:pl-3">
                            <label className="checkbox flex items-center">
                              <span
                                aria-hidden="true"
                                className="ml-2 flex sm:block"
                              >
                                {t("support.all_results")}
                              </span>
                              <input
                                aria-label="Filter by all results"
                                type="checkbox"
                                checked={selectedFilters.length === 0}
                                onChange={clearAllFilters}
                              />
                              <span className="checkmark" />
                            </label>
                          </div>
                          {flatFacets.map((f, i) => {
                            const name = f.name || "Other";
                            const checked = selectedFilters.includes(name);
                            return (
                              <div
                                key={`flat-${i}`}
                                className="relative mb-2 md:pl-3"
                              >
                                <label className="checkbox flex items-center">
                                  <span
                                    aria-hidden="true"
                                    className="ml-2 flex sm:block"
                                  >{`${name} (${f.count})`}</span>
                                  <input
                                    aria-label={`Filter by ${name}`}
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFacet(name)}
                                  />
                                  <span className="checkmark" />
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex contain-fluid mt-8 grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12">
                    <div class="filter_btn_height fixed pin-b cols-11 pin-l border-t bg-white block sm:hidden border-b border-grey-2 flex px-2 pt-4 pb-3">
                      <div
                        class="cursor-pointer py-2 text-center w-full text-xs bg-black text-white border border-black font-normal hover:bg-grey-4 hover:border-grey-4 close-productList-filter"
                        onClick={() => setIsOpenMobFiltr(false)}
                      >
                        Show Results
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* RIGHT: results */}
              <div className="cols-14 sm:cols-10 sm:offs-1 mt-16 sm:mt-0">
                <div ref={productsTopRef} style={{ scrollMarginTop: 80 }} />
                <h2 className="related-products-title uppercase text-xs bg-g-blue-1 px-5 py-1 mb-3 text-white inline-block font-normal leading-normal heading6">
                  {t("support.products_label")}
                </h2>
                <ProductsStrip
                  docs={resultProducts}
                  refStrip={resultsStripRef}
                />
                <ul
                  id="searchResultContainerDiv"
                  className="search-results list-group pl-0"
                >
                  {results.list
                    .filter((d) => {
                      const st = Array.isArray(d?.filterSubDocumentType)
                        ? d.filterSubDocumentType[0]
                        : d?.filterSubDocumentType;
                      return String(st || "").toLowerCase() !== "variant";
                    })
                    .map((d, i) => {
                      const href = toAbs(d?.url || d?.id);
                      const rawTitle = Array.isArray(d?.title)
                        ? d.title[0]
                        : d?.title || href;
                      const title = stripHtml(rawTitle);
                      const desc = stripHtml(d?.description);
                      const subType = Array.isArray(d?.filterSubDocumentType)
                        ? d.filterSubDocumentType[0]
                        : d?.filterSubDocumentType;
                      const isPdf = /\.pdf($|\?)/i.test(href);
                      return (
                        <li
                          key={`doc-${i}`}
                          className="cols-14 sm:cols-10 sm:offs-1 mt-16 list-reset"
                        >
                          <article className="mb-16">
                            <div className="text-gray-400 mb-4">
                              {subType && (
                                <span className="uppercase text-xs bg-g-blue-1 px-5 py-1 text-white inline-block font-normal mr-2">
                                  {subType}
                                </span>
                              )}
                              {isPdf && (
                                <span className="uppercase text-xs bg-g-blue-1 px-5 py-1 text-white inline-block font-normal">
                                  {t("support.pdf_badge")}
                                </span>
                              )}
                            </div>
                            <h3 className="font-normal mb-4 heading6">
                              <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                title={title}
                                className="text-black body-link-bg-black border-none"
                              >
                                {title}
                              </a>
                            </h3>
                            {desc && (
                              <p className="responsive-text-base word-wrap">
                                <span>{desc}</span>
                              </p>
                            )}
                            <p className="responsive-text-base">
                              <a
                                tabIndex={-1}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                aria-hidden="true"
                                className="text-blue border-none word-wrap"
                              >
                                {href}
                              </a>
                            </p>
                          </article>
                        </li>
                      );
                    })}
                </ul>

                {!queryLoading && resultsTotal === 0 && (
                  <div className="cols-14 sm:cols-10 sm:offs-1 mt-16">
                    <p role="alert" className="mb-12 font-normal">
                      {t("support.no_results")}
                    </p>
                  </div>
                )}

                {resultsTotal > ROWS && (
                  <div className="search-pagination-container">
                    <div className="clp-pagination-wrap">
                      <div>
                        <div className="mt-8 xs:mt-12 sm:flex justify-center items-center">
                          <ul className="list-reset flex items-center justify-center relative sm:w-500px md:w-full wd-uppercase">
                            <a
                              href="#prev"
                              onClick={(e) => {
                                e.preventDefault();
                                gotoPage(page - 1);
                              }}
                              id="prevbtn"
                              className={`new-prev-2 btnprev md:pr-12 pr-10 flex items-center absolute lg:relative pin-l-0 -pin-t-25 lg:pin-t-0 lg:pr-3 ${
                                page === 1
                                  ? "opacity-50 pointer-events-none"
                                  : ""
                              }`}
                            >
                              <span className="pagination-prev font-medium leading-tight">
                                {t("pagination.prev")}
                              </span>
                            </a>
                            {(() => {
                              const total = Math.max(
                                1,
                                Math.ceil(resultsTotal / ROWS)
                              );
                              const list = buildPageList(page, total);
                              return list.map((p, idx) =>
                                p === "dots" ? (
                                  <li
                                    key={`dots-${idx}`}
                                    className="dots mt-1 sm:mt-0 md:mx-3 mx-3 cursor-default"
                                  >
                                    <span className="dot2 h-12 w-12 inline">
                                      ...
                                    </span>
                                  </li>
                                ) : (
                                  <a
                                    key={`p-${p}`}
                                    href={`#p${p}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      gotoPage(p);
                                    }}
                                    className={`two numb mt-1 sm:mt-0 md:mx-3 -mx-1 xs:mx-1 ${
                                      p === page
                                        ? "active wd-rounded-full wd-bg-[#f2f3f3]"
                                        : ""
                                    }`}
                                  >
                                    <span className="flex justify-center numbers leading-loose inline-block cursor-pointer hover:wd-bg-[#f2f3f3] px-5 py-2 h-12 w-12 wd-rounded-full">
                                      {p}
                                    </span>
                                  </a>
                                )
                              );
                            })()}

                            <a
                              href="#next"
                              onClick={(e) => {
                                e.preventDefault();
                                gotoPage(page + 1);
                              }}
                              id="nextbtn"
                              className={`btnnext md:pl-12 pl-10 flex items-center absolute lg:relative pin-r-0 -pin-t-25 lg:pin-t-0 lg:pl-3 ${
                                page === totalPages
                                  ? "opacity-50 pointer-events-none"
                                  : ""
                              }`}
                            >
                              <span className="font-medium leading-tight">
                                {t("pagination.next")}
                              </span>
                            </a>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
